import { createRouter, createWebHistory } from "vue-router";
import { registerSidebarContent } from "@/composables/useSidebar.js";
import {
  showLoadingScreen,
  hideLoadingScreen,
} from "@/composables/useRouteLoading.js";
import { preloadComponent } from "./preload.js";
import ViewHome from "@/views/ViewHome.vue";
import ViewProfile from "@/views/ViewProfile.vue";
import ViewContact from "@/views/ViewContact.vue";
import WorkSkullcandy from "@/views/work/WorkSkullcandy.vue";
import WorkForethought from "@/views/work/WorkForethought.vue";
import WorkRealm from "@/views/work/WorkRealm.vue";
import WorkWhiteHouse from "@/views/work/WorkWhiteHouse.vue";
import WorkOtter from "@/views/work/WorkOtter.vue";
import ViewWriting from "@/views/ViewWriting.vue";
import PostFigmaForMarketers from "@/views/posts/PostFigmaForMarketers.vue";
import CheatsheetForAfterEffects from "@/views/posts/cheatsheet-for-after-effects/CheatsheetForAfterEffects.vue";
import ViewOdyssey from "@/views/work/ViewOdyssey.vue";
import ViewThankYou from "@/views/ViewThankYou.vue";
import ViewWall from "@/views/ViewWall.vue";
// Global reference to the sidebar component
let globalSidebarRef = null;
let globalSidebarUpdateFn = null;

// Function to set the global sidebar reference
export const setSidebarRef = (ref) => {
  globalSidebarRef = ref;
};

// Function to set the global sidebar update function
export const setSidebarUpdateFn = (updateFn) => {
  globalSidebarUpdateFn = updateFn;
};

const routes = [
  {
    path: "/",
    name: "Home",
    component: ViewHome,
    meta: { transition: "fade-slide" },
  },
  {
    path: "/the-wall",
    name: "Wall",
    component: ViewWall,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "The Wall",
      sidebarDescription:
        "A curated pile of design misfits that don't belong anywhere else.",
    },
  },
  {
    path: "/writing",
    name: "Writing",
    component: ViewWriting,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "Thoughts half-formed and fully typed",
      sidebarDescription:
        "A collection of things I was convinced were important at the time of writing. The views expressed herein are mine alone and not reviewed or endorsed by my employer, which, for the record, is also me.",
    },
  },
  {
    path: "/writing/figma-for-marketers",
    name: "PostFigmaForMarketers",
    component: PostFigmaForMarketers,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "Figma—not just for designers",
      sidebarDescription:
        "A no-nonsense intro for marketers, writers, and anyone who needs to get in, get out, and not screw things up.",
    },
  },
  {
    path: "/writing/a-cheat-sheet-for-after-effects",
    name: "PostIllegalHacksForAfterEffects",
    component: CheatsheetForAfterEffects,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "A cheat sheet for After Effects",
      sidebarDescription:
        "A cheat sheet of shortcuts for the most unintuitive piece of software Adobe has ever made. And that's saying a lot.",
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: ViewProfile,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "tl;dr Interfaces and sometimes code",
      sidebarDescription:
        "Multidisciplinary designer focused on product strategy, creative direction, and systems design for organizations across automotive, healthcare, tech, and AI.",
    },
  },
  {
    path: "/contact",
    name: "Contact",
    component: ViewContact,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "Send praise and / or criticism",
      sidebarDescription:
        "Projects tend to book out months in advance, but that doesn't mean we shouldn't get started right away, so get in touch. I'd love to hear from you. Really.",
    },
  },
  {
    path: "/work/skullcandy",
    name: "Skullcandy",
    component: WorkSkullcandy,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "P03_ Skullcandy Mobile & Gaming",
      sidebarDescription:
        "In April 2021 Rally Interactive — now a part of Stripe — invited me on board to shoulder a design overhaul of the Skullcandy mobile app. What followed was a project that truly allowed me to stretch all of my disciplines and work with an incredible team of engineers and partners.",
    },
  },
  {
    path: "/work/forethought",
    name: "Forethought",
    component: WorkForethought,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "P05_ Forethought",
      sidebarDescription:
        "Founded in 2017, Forethought won TechCrunch Disrupt with Agatha Answers, an AI-powered tool that integrates with help desk platforms to surface relevant information for customer support agents. The company uses deep learning to process and index large datasets across 100 languages.",
    },
  },
  {
    path: "/work/realm",
    name: "Realm",
    component: WorkRealm,
    meta: { transition: "fade-slide" },
  },
  {
    path: "/work/the-white-house",
    name: "The White House",
    component: WorkWhiteHouse,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "P04_ The White House",
      sidebarDescription:
        "TM were tasked with consolidating key information from the existing coronavirus.gov website into one easy-to-use portal in which users could type in questions and get critical information related to what was top-of-mind for the American public at the time.",
    },
  },
  {
    path: "/work/otter",
    name: "Otter",
    component: WorkOtter,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "P01_ Otter",
      sidebarDescription:
        "I led product design at Otter, a mobile app that aimed to solve last-minute childcare, allowing parents to find and book a sitter in their area for same-day or next-day care.",
    },
  },
  {
    path: "/work/odyssey",
    name: "Odyssey",
    component: ViewOdyssey,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "P02_ Odyssey",
      sidebarDescription:
        "Some projects go deeper than a standard rebrand. Odyssey needed more than new visuals — they needed a new name, a new story, and a way to tell it with confidence.",
    },
  },
  {
    path: "/how-did-you-even-get-here",
    name: "Thank You",
    component: ViewThankYou,
    meta: {
      transition: "fade-slide",
      sidebarTitle: "Thank you, Stranger.",
      sidebarDescription:
        "Thanks for reaching out. I'll get back to you within 1-2 business days. I'm glad you're here.",
    },
  },
];

// Register sidebar content for all routes at startup
routes.forEach((route) => {
  if (route.meta?.sidebarTitle && route.meta?.sidebarDescription) {
    registerSidebarContent(
      route.name,
      route.meta.sidebarTitle,
      route.meta.sidebarDescription
    );
  }
});

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Preload component assets before resolving route
router.beforeResolve(async (to, from, next) => {
  const matched = to.matched[to.matched.length - 1];
  const component = matched?.components?.default;

  try {
    // Show loading screen - total animation duration is 1.8s (0.6s down + 0.6s pause + 0.6s up)
    const loadingPromise = showLoadingScreen(1800);

    // Start preloading the component immediately
    const preloadPromise = preloadComponent(component);

    // Wait for component to be ready and initial wipe down (0.6s) to complete
    await Promise.all([
      preloadPromise,
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ]);

    // Proceed with navigation - content will be revealed when wipe up animation completes
    next();

    // Wait for entire animation sequence to finish
    await loadingPromise;
    hideLoadingScreen();
  } catch (err) {
    console.error("Failed to preload:", err);
    hideLoadingScreen();
    next();
  }
});

// Navigation guards to trigger sidebar animation and content updates
router.beforeEach(async (to, from, next) => {
  // Update sidebar content before navigation starts
  if (globalSidebarUpdateFn) {
    globalSidebarUpdateFn(to.name);
  }

  // Trigger shrink animation before navigation
  if (globalSidebarRef && globalSidebarRef.triggerAnimation) {
    globalSidebarRef.triggerAnimation();
  }

  // Scroll to top on route change
  window.scrollTo({ top: 0, behavior: "smooth" });

  next();
});

// After navigation completes, expand the overlay back
router.afterEach(() => {
  // Wait for the transition to complete
  setTimeout(() => {
    if (globalSidebarRef && globalSidebarRef.expandAnimation) {
      globalSidebarRef.expandAnimation();
    }
  }, 500); // Wait for transition to complete
});

export default router;
